import { useState } from 'react';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { Phone } from '@phosphor-icons/react/dist/ssr/Phone';
import { Envelope } from '@phosphor-icons/react/dist/ssr/Envelope';
import { InstagramLogo } from '@phosphor-icons/react/dist/ssr/InstagramLogo';
import { COLORS, SPACING, TYPOGRAPHY } from '../../../styles/UISystem';

/**
 * SelectionRow Component
 *
 * Toggleable row for selection lists (contact sharing, type pickers, etc.)
 * Icon + label on left, optional checkmark on right.
 *
 * Selected: darker background (#F0F0F0), darker icon/text (#5A5A5A), checkmark visible
 * Unselected: light background (#FAFAFA), lighter icon/text (#BEBEBE), checkmark hidden
 *
 * Checkmark uses opacity toggle (not conditional render) to reserve space and prevent text shifting.
 *
 * @param {Component} icon - Phosphor icon component (or null for no icon)
 * @param {string} label - Display text
 * @param {boolean} isSelected - Controls highlight state
 * @param {function} onClick - Click handler
 * @param {boolean} showCheckmark - Whether to show checkmark space (default true)
 * @param {function} onDelete - When present, renders a delete X on the right. For user-created items.
 */
export const SelectionRow = ({
  icon: Icon,
  label,
  isSelected,
  onClick,
  showCheckmark = true,
  onDelete,
  // 'detail' = the inline shared ledger on a white page; default = bottom sheet
  surface = 'sheet',
}) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      padding: '12px 16px',
      // The detail page's shared ledger sits on white, where a grey fill reads
      // as a locked contact row. The bottom sheet keeps grey: on its dimmed
      // backdrop the same fill reads correctly (frames, 2026-08-18).
      backgroundColor: surface === 'detail' ? '#FFFFFF' : isSelected ? '#F0F0F0' : '#FAFAFA',
      border: surface === 'detail' ? '0.5px solid #E0E0E0' : 'none',
      borderRadius: '24px',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      width: '100%',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {Icon && <Icon size={20} color={isSelected ? '#5A5A5A' : '#BEBEBE'} weight="regular" />}
      <span
        style={{
          fontSize: '16px',
          fontWeight: '400',
          color: isSelected ? '#5A5A5A' : '#BEBEBE',
        }}
      >
        {label}
      </span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {showCheckmark && (
        <Check size={20} color="#5A5A5A" weight="bold" style={{ opacity: isSelected ? 1 : 0 }} />
      )}
      {onDelete && (
        <X
          size={16}
          color="#999"
          weight="bold"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{ cursor: 'pointer' }}
        />
      )}
    </div>
  </div>
);

/**
 * SelectionRowExample Component
 *
 * Example component for the UI Showcase demonstrating SelectionRow states.
 */
export const SelectionRowExample = () => {
  const [selected, setSelected] = useState({ phone: true, email: false, instagram: true });

  return (
    <div style={{ marginBottom: SPACING.XL }}>
      <div
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: COLORS.DARK_GRAY,
          marginBottom: SPACING.L,
          textAlign: 'center',
        }}
      >
        SelectionRow
      </div>

      {/* With checkmarks */}
      <div
        style={{
          marginBottom: SPACING.L,
          fontSize: TYPOGRAPHY.HEADER_SMALL.fontSize,
          color: COLORS.DARK_GRAY,
          textAlign: 'center',
        }}
      >
        Multi-select (with checkmarks)
      </div>
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: SPACING.L }}
      >
        <SelectionRow
          icon={Phone}
          label="Phone"
          isSelected={selected.phone}
          onClick={() => setSelected((s) => ({ ...s, phone: !s.phone }))}
        />
        <SelectionRow
          icon={Envelope}
          label="Email"
          isSelected={selected.email}
          onClick={() => setSelected((s) => ({ ...s, email: !s.email }))}
        />
        <SelectionRow
          icon={InstagramLogo}
          label="Instagram"
          isSelected={selected.instagram}
          onClick={() => setSelected((s) => ({ ...s, instagram: !s.instagram }))}
        />
      </div>

      {/* Without checkmarks (single-select style) */}
      <div
        style={{
          marginBottom: SPACING.L,
          fontSize: TYPOGRAPHY.HEADER_SMALL.fontSize,
          color: COLORS.DARK_GRAY,
          textAlign: 'center',
        }}
      >
        Single-select (no checkmarks)
      </div>
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: SPACING.L }}
      >
        <SelectionRow
          icon={Phone}
          label="Phone"
          isSelected={true}
          onClick={() => {}}
          showCheckmark={false}
        />
        <SelectionRow
          icon={Envelope}
          label="Email"
          isSelected={false}
          onClick={() => {}}
          showCheckmark={false}
        />
        <SelectionRow
          icon={null}
          label="None"
          isSelected={false}
          onClick={() => {}}
          showCheckmark={false}
        />
      </div>
    </div>
  );
};
